import type { $Fetch } from 'ofetch';
import type {
  RequerimentoAdminGateway, ListRequerimentoTiposParams,
  ListRequerimentosParams, FilaRequerimentoParams,
} from '@/application/port/out/RequerimentoAdminGateway';
import type {
  Requerimento, RequerimentoTipo, RequerimentoEtapa,
  RequerimentoHistorico, RequerimentoPagamento,
  RequerimentoTipoCommand, DecidirEtapaCommand,
  PedirComplementacaoCommand, ReverterRequerimentoCommand,
} from '@/domain/model/Requerimento';
import type { Page } from '@/domain/model/Page';
import type { ApiResponse } from '@/infrastructure/http/types';
import { assertApiSuccess } from '@/infrastructure/http/assertApiSuccess';

/**
 * HTTP gateway do admin de Requerimento (acadadmin slice).
 * Tipos `/api/v1/admin/requerimento-tipos`; triagem/workflow/pagamento `/api/v1/admin/requerimentos`;
 * detalhe/histórico `/api/v1/requerimentos`.
 *
 * @since G.5.15
 */
export class HttpRequerimentoAdminGateway implements RequerimentoAdminGateway {
  private readonly tipos = '/api/v1/admin/requerimento-tipos';
  private readonly admin = '/api/v1/admin/requerimentos';
  private readonly base = '/api/v1/requerimentos';
  constructor(private readonly client: $Fetch) {}

  async listTipos(params: ListRequerimentoTiposParams): Promise<Page<RequerimentoTipo>> {
    const res = await this.client<ApiResponse<Page<RequerimentoTipo>>>(
      this.tipos, { method: 'GET', params: {
        onlyActive: params.onlyActive ?? false, page: params.page ?? 0, size: params.size ?? 50,
      } });
    assertApiSuccess(res);
    return res.data;
  }

  async createTipo(cmd: RequerimentoTipoCommand): Promise<RequerimentoTipo> {
    const res = await this.client<ApiResponse<RequerimentoTipo>>(this.tipos, { method: 'POST', body: cmd });
    assertApiSuccess(res);
    return res.data;
  }

  async updateTipo(id: string, cmd: RequerimentoTipoCommand): Promise<RequerimentoTipo> {
    const res = await this.client<ApiResponse<RequerimentoTipo>>(
      `${this.tipos}/${encodeURIComponent(id)}`, { method: 'PUT', body: cmd });
    assertApiSuccess(res);
    return res.data;
  }

  async deactivateTipo(id: string): Promise<void> {
    // DELETE 204 No Content.
    await this.client(`${this.tipos}/${encodeURIComponent(id)}`, { method: 'DELETE' });
  }

  async listFiltered(params: ListRequerimentosParams): Promise<Page<Requerimento>> {
    const query: Record<string, string | number> = { page: params.page ?? 0, size: params.size ?? 50 };
    if (params.status) query.status = params.status;
    const res = await this.client<ApiResponse<Page<Requerimento>>>(this.admin, { method: 'GET', params: query });
    assertApiSuccess(res);
    return res.data;
  }

  async get(id: string): Promise<Requerimento> {
    const res = await this.client<ApiResponse<Requerimento>>(
      `${this.base}/${encodeURIComponent(id)}`, { method: 'GET' });
    assertApiSuccess(res);
    return res.data;
  }

  async historico(id: string): Promise<RequerimentoHistorico[]> {
    const res = await this.client<ApiResponse<RequerimentoHistorico[]>>(
      `${this.base}/${encodeURIComponent(id)}/historico`, { method: 'GET' });
    assertApiSuccess(res);
    return res.data;
  }

  async fila(params: FilaRequerimentoParams): Promise<Page<RequerimentoEtapa>> {
    const res = await this.client<ApiResponse<Page<RequerimentoEtapa>>>(
      `${this.admin}/fila`, { method: 'GET', params: {
        papel: params.papel, page: params.page ?? 0, size: params.size ?? 50,
      } });
    assertApiSuccess(res);
    return res.data;
  }

  async claimEtapa(etapaId: string): Promise<RequerimentoEtapa> {
    const res = await this.client<ApiResponse<RequerimentoEtapa>>(
      `${this.admin}/etapas/${encodeURIComponent(etapaId)}/claim`, { method: 'POST' });
    assertApiSuccess(res);
    return res.data;
  }

  async decidirEtapa(etapaId: string, cmd: DecidirEtapaCommand): Promise<Requerimento> {
    const res = await this.client<ApiResponse<Requerimento>>(
      `${this.admin}/etapas/${encodeURIComponent(etapaId)}/decidir`, { method: 'POST', body: cmd });
    assertApiSuccess(res);
    return res.data;
  }

  async pedirComplementacao(id: string, cmd: PedirComplementacaoCommand): Promise<Requerimento> {
    const res = await this.client<ApiResponse<Requerimento>>(
      `${this.admin}/${encodeURIComponent(id)}/complementacao`, { method: 'POST', body: cmd });
    assertApiSuccess(res);
    return res.data;
  }

  async reenviar(id: string): Promise<Requerimento> {
    const res = await this.client<ApiResponse<Requerimento>>(
      `${this.admin}/${encodeURIComponent(id)}/reenviar`, { method: 'POST' });
    assertApiSuccess(res);
    return res.data;
  }

  async reverter(id: string, cmd: ReverterRequerimentoCommand): Promise<Requerimento> {
    const res = await this.client<ApiResponse<Requerimento>>(
      `${this.admin}/${encodeURIComponent(id)}/reverter`, { method: 'POST', body: cmd });
    assertApiSuccess(res);
    return res.data;
  }

  async getPagamento(id: string): Promise<RequerimentoPagamento | null> {
    // Requerimento de tipo sem taxa → 200 com data null (não 404).
    const res = await this.client<ApiResponse<RequerimentoPagamento | null>>(
      `${this.admin}/${encodeURIComponent(id)}/pagamento`, { method: 'GET' });
    assertApiSuccess(res);
    return res.data;
  }

  async confirmarPagamento(id: string): Promise<RequerimentoPagamento> {
    const res = await this.client<ApiResponse<RequerimentoPagamento>>(
      `${this.admin}/${encodeURIComponent(id)}/pagamento/confirmar`, { method: 'POST' });
    assertApiSuccess(res);
    return res.data;
  }
}
